import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './LandingPage.css';
import AuthModal from '../AuthModal/AuthModal';

function LandingPage() {
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('signup'); // 'signup', 'login', or 'login-password'
  const [inputText, setInputText] = useState(''); // Add state for input text
  const [showComingSoon, setShowComingSoon] = useState(false); // State to control coming soon message
  const [comingSoonMessage, setComingSoonMessage] = useState(''); // Message content
  const [comingSoonPosition, setComingSoonPosition] = useState({ top: 0, left: 0 }); // Position for the message
  const navigate = useNavigate();
  
  // States for the typing effect
  const [displayedIntro, setDisplayedIntro] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const fullIntroText = "  Hello! I'm your INTP AI companion. I love exploring ideas and theoretical concepts. What would you like to discuss today?";
  const typingTimerRef = useRef(null);

  // Typing effect implementation
  useEffect(() => {
    let index = 0;
    setIsTyping(true);
    setDisplayedIntro('');
    
    // Clear any existing timer
    if (typingTimerRef.current) {
      clearInterval(typingTimerRef.current);
    }
    
    typingTimerRef.current = setInterval(() => {
      if (index < fullIntroText.length) {
        setDisplayedIntro(prev => prev + fullIntroText.charAt(index));
        index++;
      } else {
        clearInterval(typingTimerRef.current);
        setIsTyping(false);
      }
    }, 30); // Speed of typing
    
    // Cleanup function
    return () => {
      if (typingTimerRef.current) {
        clearInterval(typingTimerRef.current);
      }
    };
  }, []); // Run once on component mount

  const openAuthModal = (mode) => {
    setAuthMode(mode);
    setShowAuthModal(true);
  };

  const closeAuthModal = () => {
    setShowAuthModal(false);
  };

  // Handle chat input button/Enter key
  const handleChatSubmit = (e) => {
    if (e) e.preventDefault();
    openAuthModal('signup');
  };

  // Handle coming soon features
  const handleComingSoon = (e, featureName) => {
    e.preventDefault();
    
    // Calculate position for the message
    const rect = e.currentTarget.getBoundingClientRect();
    setComingSoonPosition({
      top: rect.bottom + window.scrollY + 10, // Add some spacing
      left: rect.left + window.scrollX + rect.width / 2
    });
    
    setComingSoonMessage(`${featureName} feature coming soon!`);
    setShowComingSoon(true);
    
    // Hide the message after 3 seconds
    setTimeout(() => {
      setShowComingSoon(false);
    }, 3000);
  };

  return (
    <div className="landing-page">
      {/* Navigation Bar */}
      <nav className="navbar">
        <div className="navbar-left">
          <img 
            src="/images/typetalk.svg" 
            alt="TypeTalk Logo" 
            className="logo" 
            style={{width: 'auto', height: '28px'}} /* Ensure consistent sizing */
          />
          <div className="navbar-links">
            <a href="#" className="nav-link coming-soon" onClick={(e) => handleComingSoon(e, "About")}>
              About <span className="soon-badge">Soon</span>
            </a>
            <a href="#" className="nav-link coming-soon" onClick={(e) => handleComingSoon(e, "Community")}>
              Community <span className="soon-badge">Soon</span>
            </a>
            <a href="#" className="nav-link coming-soon" onClick={(e) => handleComingSoon(e, "Blog")}>
              Blog <span className="soon-badge">Soon</span>
            </a>
          </div>
        </div>
        <div className="navbar-right">
          <button onClick={() => openAuthModal('login')} className="auth-button login-button">
            Log In
          </button>
          <button onClick={() => openAuthModal('signup')} className="auth-button signup-button">
            Sign Up
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <h1 className="hero-title">Talk To Your Type, Explore Others</h1>
          <p className="hero-subtitle">
            Ever wondered how your MBTI personality would text you back? Now you can with TypeTalk!
          </p>
          <div className="chat-preview">
            <div className="chat-message ai-message">
              <div className="message-avatar">
                <img src="/images/INTP_1.png" alt="INTP Avatar" />
              </div>
              <div className="message-content">
                <p>
                  {displayedIntro}
                  {isTyping && <span className="cursor-blink"></span>}
                </p>
              </div>
            </div>
          </div>
          
          {/* Simplified chat input - single-level structure */}
          <div className="landing-chat-input">
            <input 
              type="text" 
              placeholder="Ask a question to start a conversation..."
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleChatSubmit();
                }
              }}
            />
            <button 
              type="button" 
              className={`send-button ${inputText.trim() ? 'active' : ''}`}
              onClick={handleChatSubmit}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M2 21L22 12L2 3L2 10L16 12L2 14L2 21Z" fill="currentColor"/>
              </svg>
            </button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <h2 className="section-title">Why Choose TypeTalk?</h2>
        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon">🌀</div>
            <h3 className="feature-title">Talk to All 16 Types</h3>
            <p className="feature-description">Explore every MBTI personality, one chat at a time.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">💬</div>
            <h3 className="feature-title">Convos That Feel Real</h3>
            <p className="feature-description">No robotic replies. Just vibes and valid insights.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">🔎</div>
            <h3 className="feature-title">Figure Yourself Out</h3>
            <p className="feature-description">Self-discovery without the cringe.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">👥</div>
            <h3 className="feature-title">Your Type, Your Guide</h3>
            <p className="feature-description">1-on-1 sessions with mentors who get how you think. Built for self-discovery, career design, and real-life direction (coming soon).</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-sections">
            <div className="footer-section">
              <h4>Company</h4>
              <a href="#" className="coming-soon" onClick={(e) => handleComingSoon(e, "About")}>About</a>
              <a href="#" className="coming-soon" onClick={(e) => handleComingSoon(e, "Contact")}>Contact</a>
            </div>
            <div className="footer-section">
              <h4>Resources</h4>
              <a href="#" className="coming-soon" onClick={(e) => handleComingSoon(e, "Blog")}>Blog</a>
              <a href="#" className="coming-soon" onClick={(e) => handleComingSoon(e, "Guides")}>Guides</a>
              <a href="#" className="coming-soon" onClick={(e) => handleComingSoon(e, "Support")}>Support</a>
            </div>
            <div className="footer-section">
              <h4>Legal</h4>
              <a href="#" className="coming-soon" onClick={(e) => handleComingSoon(e, "Terms")}>Terms</a>
              <a href="#" className="coming-soon" onClick={(e) => handleComingSoon(e, "Privacy")}>Privacy</a>
            </div>
          </div>
          <div className="footer-bottom">
            <img src="/images/typetalk.svg" alt="TypeTalk Logo" className="logo" />
            <p>© 2025 TypeTalk. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Auth Modal */}
      {showAuthModal && (
        <AuthModal 
          mode={authMode} 
          onClose={closeAuthModal} 
          onSwitchMode={(mode) => setAuthMode(mode)}
        />
      )}

      {/* Coming Soon Message */}
      {showComingSoon && (
        <div 
          className="coming-soon-popup"
          style={{
            top: `${comingSoonPosition.top}px`,
            left: `${comingSoonPosition.left}px`
          }}
        >
          {comingSoonMessage}
        </div>
      )}
    </div>
  );
}

export default LandingPage;
